import { useEffect, useState } from 'react'
import styled from 'styled-components'

import {
  Dialog,
  Layout,
  Radio,
  RadioGroup,
  Text,
  type RadioGroupChangeEventDetail,
} from '@loadsmart/miranda-react'

import { type OpenDockCancelOrResetOption } from '../../use-cases/openDockCancelDialogMutation'

export type CancelAppointmentOption = OpenDockCancelOrResetOption

type AppointmentCancelDialogProps = {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly isLoading: boolean
  readonly onConfirm: (option: CancelAppointmentOption) => void
  readonly resetOnly?: boolean
}

const StyledRadio = styled(Radio)`
  --m-radio-outline-color: transparent;
  flex-shrink: 0;
  outline: none;
`

export function AppointmentCancelDialog({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  resetOnly = false,
}: AppointmentCancelDialogProps) {
  const [selected, setSelected] = useState<CancelAppointmentOption>('cancel')

  useEffect(() => {
    if (isOpen) {
      setSelected(resetOnly ? 'reset' : 'cancel')
    }
  }, [isOpen, resetOnly])

  function handleClose() {
    if (!isLoading) {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} size="small" onClose={handleClose}>
      <Dialog.Header>
        <Text variant="heading-md-bold">Cancel appointment</Text>
      </Dialog.Header>
      <Dialog.Body>
        <Layout.Stack gap="spacing-6">
          <Text variant="body-md">Choose how you want to cancel this appointment</Text>
          <RadioGroup
            orientation="vertical"
            value={selected}
            onChange={({ detail }: CustomEvent<RadioGroupChangeEventDetail>) => {
              setSelected(detail.value as CancelAppointmentOption)
            }}
          >
            <Layout.Stack gap="spacing-4">
              <Layout.Box
                padding="spacing-4"
                borderWidth="border-thin"
                borderRadius="border-radius-s"
                borderColor="color-border"
                onClick={() => !isLoading && !resetOnly && setSelected('cancel')}
              >
                <Layout.Group align="center" gap="spacing-3" style={{ flexWrap: 'nowrap' }}>
                  <StyledRadio
                    value="cancel"
                    checked={selected === 'cancel'}
                    disabled={isLoading || resetOnly}
                  />
                  <Layout.Stack gap="spacing-2">
                    <Text variant="body-md-bold">Cancel appointment</Text>
                    <Text variant="body-sm" color="color-text-tertiary">
                      The appointment status will be set to{' '}
                      <Text variant="body-sm" color="color-danger-60">
                        Canceled
                      </Text>
                    </Text>
                  </Layout.Stack>
                </Layout.Group>
              </Layout.Box>
              <Layout.Box
                padding="spacing-4"
                borderWidth="border-thin"
                borderRadius="border-radius-s"
                borderColor="color-border"
                onClick={() => !isLoading && setSelected('reset')}
              >
                <Layout.Group align="center" gap="spacing-3" style={{ flexWrap: 'nowrap' }}>
                  <StyledRadio value="reset" checked={selected === 'reset'} disabled={isLoading} />
                  <Layout.Stack gap="spacing-2">
                    <Text variant="body-md-bold">Reset appointment</Text>
                    <Text variant="body-sm" color="color-text-tertiary">
                      The appointment will return to its initial status, so you can restart the
                      scheduling process
                    </Text>
                  </Layout.Stack>
                </Layout.Group>
              </Layout.Box>
            </Layout.Stack>
          </RadioGroup>
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionSecondary variant="tertiary" onClick={handleClose} disabled={isLoading}>
          Return
        </Dialog.ActionSecondary>
        <Dialog.ActionPrimary
          variant="danger"
          onClick={() => onConfirm(selected)}
          loading={isLoading}
          disabled={isLoading}
        >
          Confirm
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>
  )
}
